import React, { useCallback } from "react";
import { toast } from "react-toastify";
import {
  EmployeeOfferDetails,
  MonetaryDetailsV2,
  PaymentDetails,
} from "../../../../types/Transaction";
import { ButtonPrimary1, HStack, VStack } from "../../../../components/utils";
import { isPaymentAmountComplete } from "../../../../utils/paymentCompletion";

type PreviewPaymentDetailsProps = {
  monetaryDetails: MonetaryDetailsV2;
  onClose: () => void;
  employee?: EmployeeOfferDetails;
  paymentDetails?: PaymentDetails[];
};

const paymentStatusConfig: Record<
  string,
  { label: string; className: string }
> = {
  PAYMENT_PENDING: {
    label: "Pending Approval",
    className: "bg-yellow-100 text-yellow-800 border border-yellow-200",
  },
  SUCCESS: {
    label: "Approved",
    className: "bg-green-100 text-green-800 border border-green-200",
  },
  FAILED: {
    label: "Rejected",
    className: "bg-red-100 text-red-800 border border-red-200",
  },
};

const StatusBadge = ({ status }: { status?: string }) => {
  if (!status) return null;
  const config = paymentStatusConfig[status] || {
    label: status,
    className: "bg-gray-100 text-gray-700",
  };
  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${config.className}`}
    >
      {config.label}
    </span>
  );
};

const formatDateDisplay = (dateStr: string | Date | undefined): string => {
  if (!dateStr) return "Not available";
  const d = new Date(dateStr as string);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isEsopFinancing = (p: PaymentDetails) =>
  p.metaData?.financingType === "ESOP_FINANCING";

const isZeroAmountSuccessfulTaxPayment = (p: PaymentDetails) =>
  p.paymentType.toLowerCase() === "tax price" &&
  p.amount === 0 &&
  p.paymentStatus === "SUCCESS";

const sortByPaymentDate = (tranches: PaymentDetails[]) =>
  [...tranches].sort(
    (a, b) =>
      new Date(a.paymentDate ?? 0).getTime() -
      new Date(b.paymentDate ?? 0).getTime()
  );

// Payment table (read-only, no approve/reject actions)

const PaymentTable = ({
  tranches,
  onDownload,
}: {
  tranches: PaymentDetails[];
  onDownload: (s3Url: string) => void;
}) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
    <table className="w-full text-sm text-left">
      <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
        <tr>
          <th className="px-4 py-3 font-medium">#</th>
          <th className="px-4 py-3 font-medium">Reference ID</th>
          <th className="px-4 py-3 font-medium">Date</th>
          <th className="px-4 py-3 text-right font-medium">Amount</th>
          <th className="px-4 py-3 font-medium">Status</th>
          <th className="px-4 py-3 font-medium">Payment Mode</th>
          <th className="px-4 py-3 font-medium">Financing</th>
          <th className="px-4 py-3 font-medium">Remarks</th>
          <th className="px-4 py-3 font-medium">Proof</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {tranches.map((tranche, idx) => (
          <tr key={tranche.id ?? idx} className="hover:bg-gray-50">
            <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
            <td className="px-4 py-3 font-medium text-gray-900">
              {isZeroAmountSuccessfulTaxPayment(tranche)
                ? ""
                : tranche.referenceId || "Not available"}
            </td>
            <td className="px-4 py-3 text-gray-600">
              {formatDateDisplay(tranche.paymentDate)}
            </td>
            <td className="px-4 py-3 text-right font-semibold text-gray-900">
              ₹{(tranche.amount ?? 0).toLocaleString("en-IN")}
            </td>
            <td className="px-4 py-3">
              <StatusBadge status={tranche.paymentStatus} />
            </td>
            <td className="px-4 py-3 text-gray-600">
              {isZeroAmountSuccessfulTaxPayment(tranche)
                ? ""
                : tranche.metaData?.modeOfPayment || "Not available"}
            </td>
            <td className="px-4 py-3">
              {isZeroAmountSuccessfulTaxPayment(
                tranche
              ) ? null : isEsopFinancing(tranche) ? (
                <VStack className="gap-0.5">
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 w-fit">
                    ESOP Financing
                  </span>
                  {tranche.metaData?.financierName && (
                    <span className="text-xs text-gray-500">
                      {tranche.metaData.financierName}
                    </span>
                  )}
                </VStack>
              ) : (
                <span className="text-xs text-gray-400">Self</span>
              )}
            </td>
            <td className="px-4 py-3 max-w-[220px]">
              <span className="text-gray-600 whitespace-pre-wrap">
                {tranche.metaData?.remarks || "Not available"}
              </span>
            </td>
            <td className="px-4 py-3">
              {tranche.documentLinks && tranche.documentLinks.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                  {tranche.documentLinks.map((doc, di) =>
                    doc.s3Url ? (
                      <button
                        key={doc.id ?? di}
                        type="button"
                        className="text-primary hover:text-primaryHover hover:underline text-left truncate max-w-[150px] transition-colors focus:outline-none focus:ring-1 focus:ring-primary rounded px-1 -ml-1"
                        onClick={() => onDownload(doc.s3Url!)}
                        title={doc.name || "View"}
                      >
                        {doc.name || `File ${di + 1}`}
                      </button>
                    ) : null
                  )}
                </div>
              ) : (
                <span className="text-gray-400 italic">No proof</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const PreviewPaymentDetails = (props: PreviewPaymentDetailsProps) => {
  const { monetaryDetails, employee, onClose, paymentDetails = [] } = props;

  const exercisePaymentLink = monetaryDetails.paymentLink;
  const taxPaymentLink = monetaryDetails.taxPaymentLink;

  const exerciseTranches = sortByPaymentDate(
    paymentDetails.filter(
      (p) => p.paymentType.toLowerCase() === "exercise price"
    )
  );
  const taxTranches = sortByPaymentDate(
    paymentDetails.filter((p) => p.paymentType.toLowerCase() === "tax price")
  );

  const exercisePaymentConfigured = exercisePaymentLink != null;
  const taxPaymentConfigured =
    !!monetaryDetails.taxPaymentEnabled && taxPaymentLink != null;
  const showExerciseSection =
    exercisePaymentConfigured || exerciseTranches.length > 0;
  const showTaxSection = taxPaymentConfigured || taxTranches.length > 0;

  const totalApprovedExercise = exerciseTranches
    .filter((p) => p.paymentStatus === "SUCCESS")
    .reduce((s, p) => s + (p.amount ?? 0), 0);
  const totalRequiredExercise =
    (employee as any)?.transactionCompensationDetails?.totalStrikePrice ?? 0;
  const isExerciseFullyPaid = isPaymentAmountComplete(
    totalRequiredExercise,
    totalApprovedExercise
  );

  const totalApprovedTax = taxTranches
    .filter((p) => p.paymentStatus === "SUCCESS")
    .reduce((s, p) => s + (p.amount ?? 0), 0);
  const totalRequiredTax =
    (employee as any)?.transactionCompensationDetails?.totalTaxAmount ?? 0;
  const isTaxFullyPaid = isPaymentAmountComplete(
    totalRequiredTax,
    totalApprovedTax
  );

  const handleDownload = useCallback((s3Url: string) => {
    if (s3Url) window.open(s3Url, "_blank");
    else toast.error("Failed to get download link");
  }, []);

  return (
    <div className="max-w-5xl w-full mx-auto bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200">
        <div>
          <h6 className="text-lg font-semibold text-gray-900">
            Preview Payment Details
          </h6>
          <p className="mt-1 text-sm text-gray-500">
            {employee?.employeeName || "Employee"}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/20 rounded p-1"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <VStack className="gap-6 p-6">
        {!showExerciseSection && !showTaxSection && (
          <div className="py-12 text-sm text-center text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            No payment details configured for this transaction.
          </div>
        )}

        {/* ── Exercise Payment Section ───────────────────────── */}
        {showExerciseSection && (
          <VStack className="gap-4">
            <HStack className="items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">
                Exercise Payment
              </span>
              {totalRequiredExercise > 0 && (
                <span className="text-sm text-gray-500">
                  Approved:{" "}
                  <span
                    className={`font-semibold ${
                      isExerciseFullyPaid ? "text-green-600" : "text-primary"
                    }`}
                  >
                    ₹{totalApprovedExercise.toLocaleString("en-IN")}
                  </span>
                  {" / "}₹{totalRequiredExercise.toLocaleString("en-IN")}
                </span>
              )}
            </HStack>

            {totalRequiredExercise > 0 && exerciseTranches.length > 0 && (
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    isExerciseFullyPaid ? "bg-green-600" : "bg-primary"
                  }`}
                  style={{
                    width: `${Math.min(
                      100,
                      (totalApprovedExercise / totalRequiredExercise) * 100
                    )}%`,
                  }}
                />
              </div>
            )}

            {exerciseTranches.length > 0 ? (
              <PaymentTable
                tranches={exerciseTranches}
                onDownload={handleDownload}
              />
            ) : (
              <div className="text-sm text-gray-400 py-2">
                No payment details yet.
              </div>
            )}
          </VStack>
        )}

        {/* ── Tax Payment Section ───────────────────────────── */}
        {showTaxSection && (
          <VStack className="gap-4">
            <HStack className="items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">
                Tax Payment
              </span>
              {totalRequiredTax > 0 && (
                <span className="text-sm text-gray-500">
                  Approved:{" "}
                  <span
                    className={`font-semibold ${
                      isTaxFullyPaid ? "text-green-600" : "text-primary"
                    }`}
                  >
                    ₹{totalApprovedTax.toLocaleString("en-IN")}
                  </span>
                  {" / "}₹{totalRequiredTax.toLocaleString("en-IN")}
                </span>
              )}
            </HStack>

            {totalRequiredTax > 0 && taxTranches.length > 0 && (
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    isTaxFullyPaid ? "bg-green-600" : "bg-primary"
                  }`}
                  style={{
                    width: `${Math.min(
                      100,
                      (totalApprovedTax / totalRequiredTax) * 100
                    )}%`,
                  }}
                />
              </div>
            )}

            {taxTranches.length > 0 ? (
              <PaymentTable
                tranches={taxTranches}
                onDownload={handleDownload}
              />
            ) : (
              <div className="text-sm text-gray-400 py-2">
                No payment details yet.
              </div>
            )}
          </VStack>
        )}

        {/* ── Footer ─────────────────────────────────────────── */}
        <HStack className="justify-end gap-2 pt-2 border-t border-gray-100">
          <ButtonPrimary1 type="button" onClick={onClose}>
            Close
          </ButtonPrimary1>
        </HStack>
      </VStack>
    </div>
  );
};

export default PreviewPaymentDetails;
